import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback } from 'react';
import cn from 'classnames';
import { ChevronDown, ChevronUp } from '../../icons';
import { useBiAction } from '../BiTracking';
import { ConditionalWrapper } from '../ConditionalWrapper';
import { useIntentBehavior } from '../IntentBehavior';
import { Tooltip } from '../Tooltip';
import styles from './Sidebar.module.scss';
function Sidebar(props) {
    const { className, children, testId } = props, rest = _object_without_properties(props, [
        "className",
        "children",
        "testId"
    ]);
    return /*#__PURE__*/ _jsx("div", _object_spread_props(_object_spread({}, rest), {
        className: cn(styles.root, className),
        "data-testid": testId,
        children: children
    }));
}
Sidebar.Header = SidebarHeader;
Sidebar.Content = SidebarContent;
Sidebar.Footer = SidebarFooter;
Sidebar.Item = SidebarItem;
Sidebar.Group = SidebarGroup;
/** Add this class to the SidebarItem to give it an active look. */ const SIDEBAR_ITEM_ACTIVE_CLASS = styles.active;
function SidebarHeader(props) {
    const { className, children, testId } = props, rest = _object_without_properties(props, [
        "className",
        "children",
        "testId"
    ]);
    return /*#__PURE__*/ _jsx("div", _object_spread_props(_object_spread({}, rest), {
        className: cn(styles.header, className),
        "data-testid": testId,
        children: children
    }));
}
function SidebarContent(props) {
    const { className, children, testId } = props, rest = _object_without_properties(props, [
        "className",
        "children",
        "testId"
    ]);
    return /*#__PURE__*/ _jsx("div", _object_spread_props(_object_spread({}, rest), {
        className: cn(styles.content, className),
        "data-testid": testId,
        children: children
    }));
}
function SidebarFooter(props) {
    const { className, children, testId } = props, rest = _object_without_properties(props, [
        "className",
        "children",
        "testId"
    ]);
    return /*#__PURE__*/ _jsx("div", _object_spread_props(_object_spread({}, rest), {
        className: cn(styles.footer, className),
        "data-testid": testId,
        children: children
    }));
}
function SidebarItem(props) {
    const { as, children, before, after, className, tooltip, intent, disabled, testId, bi, noBi: _noBi, onClick } = props, rest = _object_without_properties(props, [
        "as",
        "children",
        "before",
        "after",
        "className",
        "tooltip",
        "intent",
        "disabled",
        "testId",
        "bi",
        "noBi",
        "onClick"
    ]);
    const { getDisabled, getTooltip } = useIntentBehavior();
    const disabledFromIntent = intent ? getDisabled(intent) : false;
    const tooltipFromIntent = intent ? getTooltip(intent) : null;
    const hasTooltip = !!(tooltip === null || tooltip === void 0 ? void 0 : tooltip.title) || !!tooltipFromIntent;
    const RootElement = as || 'button';
    const logBiAction = useBiAction();
    const handleClick = useCallback((event)=>{
        if (bi === null || bi === void 0 ? void 0 : bi.name) {
            logBiAction({
                entityType: 'sidebar_item',
                entityName: bi.name,
                actionType: 'click',
                data: _object_spread_props(_object_spread({}, bi.data), {
                    test_id: testId !== null && testId !== void 0 ? testId : ''
                })
            });
        }
        return onClick === null || onClick === void 0 ? void 0 : onClick(event);
    }, [
        bi,
        logBiAction,
        onClick,
        testId
    ]);
    return /*#__PURE__*/ _jsx(ConditionalWrapper, {
        condition: hasTooltip,
        wrapper: (children)=>/*#__PURE__*/ _jsx(Tooltip, _object_spread_props(_object_spread({
                placement: "top"
            }, tooltip || {
                title: tooltipFromIntent
            }), {
                children: /*#__PURE__*/ _jsx("div", {
                    className: cn(styles.wrapper, className),
                    "data-testid": testId && "".concat(testId, "-wrapper"),
                    children: children
                })
            })),
        children: /*#__PURE__*/ _jsxs(RootElement, _object_spread_props(_object_spread({}, rest), {
            onClick: handleClick,
            disabled: disabledFromIntent || disabled,
            className: cn(styles.item, className),
            "data-testid": testId,
            children: [
                before && /*#__PURE__*/ _jsx("span", {
                    className: styles.itemPrefix,
                    children: before
                }),
                children && /*#__PURE__*/ _jsx("span", {
                    className: styles.itemContent,
                    children: children
                }),
                after && /*#__PURE__*/ _jsx("span", {
                    className: styles.itemSuffix,
                    "data-testid": testId && testId + '-suffix',
                    children: after
                })
            ]
        }))
    });
}
function SidebarGroup(props) {
    const { id, as, children, before, className, items, collapsed, onClick, testId } = props, rest = _object_without_properties(props, [
        "id",
        "as",
        "children",
        "before",
        "className",
        "items",
        "collapsed",
        "onClick",
        "testId"
    ]);
    const RootElement = as || 'button';
    return /*#__PURE__*/ _jsxs("div", {
        className: cn(styles.group, collapsed ? styles.collapsed : ''),
        children: [
            /*#__PURE__*/ _jsxs(RootElement, _object_spread_props(_object_spread({}, rest), {
                id: id,
                className: cn(styles.groupToggle, className),
                onClick: onClick,
                "data-testid": testId,
                children: [
                    before && /*#__PURE__*/ _jsx("span", {
                        className: styles.itemPrefix,
                        children: before
                    }),
                    children && /*#__PURE__*/ _jsx("span", {
                        className: styles.itemContent,
                        children: children
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: styles.itemSuffix,
                        "data-testid": testId && testId + '-suffix',
                        children: collapsed ? /*#__PURE__*/ _jsx(ChevronDown, {
                            size: 18
                        }) : /*#__PURE__*/ _jsx(ChevronUp, {
                            size: 18
                        })
                    })
                ]
            })),
            items
        ]
    });
}
export { Sidebar, SidebarHeader, SidebarContent, SidebarFooter, SidebarItem, SidebarGroup, SIDEBAR_ITEM_ACTIVE_CLASS };
